import { alertActions, pricingDrawerUiActions } from '@actions';
import { ExternalLink } from '@components/Link';
import { Typography } from '@components/Typography';
import { PRICING_DRAWER_TYPE } from '@constants/pricing-drawer-ui';
import { ExternalRoutes, RoutePath } from '@constants/routes';
import LogoutModal from '@features/LogoutModal';
import Notifications from '@features/Notifications';
import { useTranslation } from '@hooks';
import Divider from '@mui/material/Divider';
import { memo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
//import { AccIntegrationName } from '@constants/integrations';
import { ReactComponent as LogoIcon } from 'assets/images/logo.svg';
import { ReactComponent as PluspointLogo } from 'assets/images/pluspoint-logo.svg';
import {
    CampaignsIcon,
    ContactsIcon,
    CouponsIcon,
    HelpCenterIcon,
    HomeIcon,
    LogoutIcon,
    MessagingIcon,
    MicrositesIcon,
    PresenceIcon,
    ReferralIcon,
    ReviewsIcon,
    SettingsIcon,
    SocialIcon,
    SurveysIcon,
} from 'assets/images/sidebar';
import BurgerButton from './BurgerButton';
import MenuItem from './MenuItem';
import { Header, List, ListItem, LogoPrintWrapper, LogoWrapper, PluspointText, SidebarDrawer } from './styles';

const Sidebar = memo(({ isMobile }) => {
    const [isExpanded, setExpanded] = useState(false);
    const [logoutIsOpen, setLogoutIsOpen] = useState(false);

    const account = useSelector(state => state.account?.account);

    const { t, i18n } = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();

    const isMultipleStripeAcc = account?.subscriptions.length > 1;
    const productsMaxNumberExceeded =
        account?.companiesMaxCount < account?.companies?.length || account?.usersMaxCount < account?.users?.length;
    const billingExpired = account && account?.billingDiffInSeconds < 0;
    //const IFOOD_CONNECTED = account?.integrations.find(i => i.type === AccIntegrationName.iFood)?.isActive;

    const onClickUpgradeItem = () => {
        if (isMultipleStripeAcc) {
            history.push(RoutePath.AccountSettingsGeneral);
            dispatch(alertActions.error(t('common.upgradeToAdd')));
        } else {
            dispatch(pricingDrawerUiActions.setOpen({ open: true, type: PRICING_DRAWER_TYPE.UPGRADE }));
        }
    };

    const MENU_ITEMS = [
        {
            id: 'pl-menu-left-link-home',
            title: t('Sidebar.home'),
            icon: <HomeIcon />,
            to: RoutePath.Home,
        },
        {
            id: 'pl-menu-left-link-contacts',
            title: t('Sidebar.contacts'),
            icon: <ContactsIcon />,
            to: RoutePath.Contacts,
        },
        // ...(IFOOD_CONNECTED
        //     ? [
        //           {
        //               id: 'pl-menu-left-link-orders',
        //               title: t('Sidebar.ordersIfood'),
        //               icon: <OrdersIcon />,
        //               to: '/orders',
        //           },
        //       ]
        //     : []),
        {
            id: 'pl-menu-left-link-presence',
            title: t('Sidebar.presence'),
            icon: <PresenceIcon />,
            base: RoutePath.Presence,
            children: [
                { title: t('Sidebar.presenceProfiles'), to: RoutePath.PresenceProfiles },
                { title: t('Sidebar.presenceMedia'), to: RoutePath.PresenceMedia },
                { title: t('Sidebar.analytics'), to: RoutePath.PresenceAnalytics },
            ],
        },
        {
            id: 'pl-menu-left-link-reviews',
            title: t('Sidebar.reviews'),
            icon: <ReviewsIcon />,
            base: RoutePath.Reviews,
            children: [
                { title: t('Sidebar.reviewsManage'), to: RoutePath.ReviewsManage },
                {
                    title: t('Sidebar.reviewsAutomation'),
                    to: RoutePath.ReviewsAutomationAutoReplies,
                    base: RoutePath.ReviewsAutomation,
                },
                { title: t('Sidebar.reviewsWidgets'), to: RoutePath.ReviewsWidgets },
                {
                    title: t('Sidebar.analytics'),
                    to: RoutePath.ReviewsAnalyticsReviews,
                    base: RoutePath.ReviewsAnalytics,
                },
            ],
        },
        {
            id: 'pl-menu-left-link-social-posts',
            title: t('Sidebar.social'),
            icon: <SocialIcon />,
            base: RoutePath.Social,
            children: [
                { title: t('Sidebar.socialPosts'), to: RoutePath.SocialPosts },
                { title: t('Sidebar.analytics'), to: RoutePath.SocialAnalytics },
            ],
        },
        {
            id: 'pl-menu-left-link-messaging',
            title: t('Sidebar.messaging'),
            icon: <MessagingIcon />,
            base: RoutePath.Messaging,
            children: [
                { title: t('Sidebar.messagingInbox'), to: RoutePath.MessagingInbox },
                // { title: t('common.templates'), to: '/messaging/templates' },
                { title: t('Sidebar.pulseAI'), to: RoutePath.MessagingPulseAIAgents, base: RoutePath.MessagingPulseAI },
                { title: t('Sidebar.analytics'), to: RoutePath.MessagingAnalytics },
            ],
        },
        {
            id: 'pl-menu-left-link-microsites',
            title: t('Sidebar.microsites'),
            icon: <MicrositesIcon />,
            to: RoutePath.Microsites,
        },
        {
            id: 'pl-menu-left-link-feedback',
            title: t('Sidebar.surveys'),
            icon: <SurveysIcon />,
            base: RoutePath.Surveys,
            children: [
                // {
                //     title: t('Sidebar.sent'),
                //     to: '/surveys/sent',
                //     upgradeRequired: !account?.surveysEnabled,
                //     upgradeClick: onClickUpgradeItem,
                // },
                {
                    title: t('Sidebar.responses'),
                    to: RoutePath.SurveysResponses,
                    upgradeRequired: !account?.surveysEnabled,
                    upgradeClick: onClickUpgradeItem,
                },
                {
                    title: t('Sidebar.templates'),
                    to: RoutePath.SurveysTemplates,
                    upgradeRequired: !account?.surveysEnabled,
                    upgradeClick: onClickUpgradeItem,
                },
                {
                    title: t('Sidebar.analytics'),
                    to: RoutePath.SurveysAnalyticsConversion,
                    base: RoutePath.SurveysAnalytics,
                    upgradeRequired: !account?.surveysEnabled,
                    upgradeClick: onClickUpgradeItem,
                },
            ],
        },
        {
            id: 'pl-menu-left-link-campaigns',
            title: t('Sidebar.campaigns'),
            icon: <CampaignsIcon />,
            base: RoutePath.Campaigns,
            children: [
                {
                    title: t('Sidebar.campaignsOneTime'),
                    to: RoutePath.CampaignsOneTime,
                    upgradeRequired: !account?.campaignsEnabled,
                    upgradeClick: onClickUpgradeItem,
                },
                {
                    title: t('Sidebar.campaignsAutomated'),
                    to: RoutePath.CampaignsAutomated,
                    upgradeRequired: !account?.campaignsEnabled,
                    upgradeClick: onClickUpgradeItem,
                },
            ],
        },
        {
            id: 'pl-menu-left-link-coupons',
            title: t('Sidebar.coupons'),
            icon: <CouponsIcon />,
            to: RoutePath.Coupons,
            upgradeRequired: !account?.couponsEnabled,
            upgradeClick: onClickUpgradeItem,
        },
    ];

    return (
        <SidebarDrawer
            PaperProps={{ id: 'pl-menu-left-list' }}
            variant="permanent"
            anchor={isMobile ? 'top' : 'left'}
            open
            isExpanded={isExpanded}
        >
            <Header>
                <Link to={RoutePath.Home} style={{ textDecoration: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <LogoPrintWrapper>
                            <PluspointLogo />
                        </LogoPrintWrapper>
                        <LogoWrapper>
                            <LogoIcon />
                        </LogoWrapper>
                        <PluspointText>Pluspoint</PluspointText>
                    </div>
                </Link>
                <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                    {isMobile && <Notifications />}
                    {isMobile && <BurgerButton isExpanded={isExpanded} setExpanded={setExpanded} />}
                </div>
            </Header>
            <List isExpanded={isExpanded} isMobile={isMobile}>
                {MENU_ITEMS.map((link, index) => (
                    <MenuItem
                        key={index}
                        exact
                        item={link}
                        callback={() => setExpanded(false)}
                        disabled={productsMaxNumberExceeded || billingExpired}
                    />
                ))}
                <Divider sx={{ backgroundColor: '#373737', margin: '16px 0' }} />
                <ExternalLink
                    href={ExternalRoutes.HelpCenter[i18n.language]}
                    sx={{ textDecoration: 'none', width: '100%' }}
                >
                    <ListItem button onClick={() => setExpanded(false)}>
                        <span />
                        <HelpCenterIcon />
                        <Typography variant="body1">{t('Sidebar.helpCenter')}</Typography>
                    </ListItem>
                </ExternalLink>
                <MenuItem
                    exact={false}
                    item={{
                        id: 'pl-menu-left-link-settings',
                        to: RoutePath.AccountSettingsProfile,
                        title: t('common.settings'),
                        icon: <SettingsIcon />,
                    }}
                    callback={() => setExpanded(false)}
                />
                <ExternalLink href={ExternalRoutes.ReferralProgram} sx={{ textDecoration: 'none', width: '100%' }}>
                    <ListItem button>
                        <span />
                        <ReferralIcon />
                        <Typography variant="body1">{t('Sidebar.referral')}</Typography>
                    </ListItem>
                </ExternalLink>
                {isMobile && (
                    <ListItem
                        button
                        onClick={() => {
                            setExpanded(false);
                            setLogoutIsOpen(true);
                        }}
                    >
                        <span />
                        <LogoutIcon />
                        <Typography variant="body1">{t('Sidebar.logout')}</Typography>
                    </ListItem>
                )}
            </List>
            <LogoutModal isOpen={logoutIsOpen} setIsOpen={setLogoutIsOpen} />
        </SidebarDrawer>
    );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
